import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiRequestError } from './api.js';
import { groupByDirectory, type UploadCandidate } from './folder-upload.js';

/**
 * The upload engine, mounted above the router.
 *
 * It lives here rather than in the upload page because transfers have to
 * survive navigation. When this state sat in the page component, clicking
 * "Browse" unmounted it and silently abandoned every transfer in flight --
 * fine for a handful of files, ruinous when migrating a few hundred gigabytes
 * off a shared drive, which is exactly when someone wants to go and look at
 * what has arrived so far.
 *
 * Closing the tab still stops everything. There is no way around that in a
 * browser, so the page warns before it happens and every partial upload can be
 * resumed from its offset afterwards.
 */

export type UploadStatus =
  | 'queued'
  | 'hashing'
  | 'uploading'
  | 'finalizing'
  | 'done'
  | 'duplicate'
  | 'cancelled'
  | 'error';

export interface UploadItem {
  id: string;
  file: File;
  /** Directory segments relative to the chosen destination. Empty = at root. */
  segments: string[];
  /**
   * The folder chosen in the picker. The item's own subdirectory under it is
   * resolved on demand, not when queued.
   */
  destination: string | null;
  status: UploadStatus;
  sent: number;
  message?: string;
  assetId?: string;
  uploadId?: string;
}

/**
 * How many files move at once.
 *
 * Three was chosen when the server buffered each whole chunk in memory before
 * writing it, which made every extra transfer cost 64 MiB of server RAM. The
 * server streams chunks straight to disk now, so its memory no longer scales
 * with this, and the useful ceiling is much higher: a file spends real time
 * hashing locally and being copied to the NAS at the end, and during both of
 * those it is sending nothing at all. Overlapping more of them keeps the link
 * busy through those gaps.
 *
 * Adjustable because the right number depends on the link, and the person
 * moving three hundred gigabytes can see the effect immediately.
 */
export const CONCURRENCY_OPTIONS = [3, 6, 12, 24] as const;
const DEFAULT_CONCURRENCY = 6;
const CONCURRENCY_KEY = 'mediaapp.uploadConcurrency';

function loadConcurrency(): number {
  try {
    const stored = Number(localStorage.getItem(CONCURRENCY_KEY));
    if (CONCURRENCY_OPTIONS.includes(stored as (typeof CONCURRENCY_OPTIONS)[number])) {
      return stored;
    }
  } catch {
    // Private windows and blocked site data both throw on access.
  }
  return DEFAULT_CONCURRENCY;
}

/**
 * Files above this are uploaded without a pre-computed checksum.
 *
 * SubtleCrypto.digest needs the whole file in memory, and a browser tab will
 * die trying to buffer a 20 GB recording. Above the threshold we skip
 * client-side dedupe; the server still hashes as it finalizes, so correctness
 * is unaffected -- only the "skip the transfer entirely" shortcut is lost.
 */
const HASH_LIMIT_BYTES = 512 * 1024 * 1024;

async function sha256Hex(file: File): Promise<string | undefined> {
  if (file.size > HASH_LIMIT_BYTES) return undefined;
  try {
    const buffer = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buffer);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return undefined;
  }
}

export const ACTIVE_STATUSES: ReadonlySet<UploadStatus> = new Set<UploadStatus>([
  'queued',
  'hashing',
  'uploading',
  'finalizing',
]);

interface UploadContextValue {
  items: UploadItem[];
  /** Queue files for a destination folder, resolving any subfolders first. */
  enqueue: (candidates: UploadCandidate[], destination: string | null) => void;
  concurrency: number;
  setConcurrency: (value: number) => void;
  retry: (id: string) => void;
  retryAllFailed: () => void;
  cancel: (id: string) => void;
  cancelAll: () => void;
  clearFinished: () => void;
  remove: (id: string) => void;
  activeCount: number;
  failedCount: number;
  /** Bytes sent and total across everything still in flight. */
  progress: { sent: number; total: number };
}

/**
 * The next file a worker should pick up, or null when there is nothing to do.
 *
 * Pulled out of the component because it is the part that goes wrong quietly.
 * Handing the same file to two workers uploads it twice; skipping an eligible
 * one leaves the queue full and idle with no error anywhere -- which is
 * exactly how this broke once already.
 */
export function nextUploadable(
  items: readonly UploadItem[],
  claimed: ReadonlySet<string>,
  cancelled: ReadonlySet<string>,
): UploadItem | null {
  return (
    items.find(
      (item) =>
        item.status === 'queued' && !claimed.has(item.id) && !cancelled.has(item.id),
    ) ?? null
  );
}

/**
 * Bytes sent and bytes outstanding across a whole batch.
 *
 * Counting only the files currently in flight made the percentage fall as work
 * completed: a finished file left the active set, taking its bytes out of both
 * halves of the fraction, and the untouched file that replaced it started at
 * zero. Five of ten files done could read lower than three of ten, which looks
 * exactly like uploads running backwards.
 *
 * Finished files therefore count as fully sent, and the number only moves
 * backwards when there is genuinely more work than before -- files added, or a
 * failure retried.
 */
export function computeProgress(items: readonly UploadItem[]): { sent: number; total: number } {
  let sent = 0;
  let total = 0;

  for (const item of items) {
    // A cancelled file is not outstanding work and must not hold the total
    // below 100% for the rest of the session.
    if (item.status === 'cancelled') continue;

    total += item.file.size;
    sent += item.status === 'done' || item.status === 'duplicate' ? item.file.size : item.sent;
  }

  return { sent, total };
}

const UploadContext = createContext<UploadContextValue | null>(null);

export function UploadProvider({ children }: { children: ReactNode }): ReactNode {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [concurrency, setConcurrencyState] = useState<number>(loadConcurrency);

  // Read by the worker loop, which captured its value when it started.
  const concurrencyRef = useRef(concurrency);
  concurrencyRef.current = concurrency;

  const setConcurrency = useCallback((value: number): void => {
    setConcurrencyState(value);
    try {
      localStorage.setItem(CONCURRENCY_KEY, String(value));
    } catch {
      // Not being able to remember the choice is not worth failing over.
    }
  }, []);

  // A ref mirror lets the worker loop read live state without being recreated
  // on every progress tick.
  const itemsRef = useRef<UploadItem[]>([]);
  itemsRef.current = items;

  /**
   * Ids already handed to a worker for their current attempt.
   *
   * Claimed synchronously, and deliberately not released on completion. The
   * queue is read from a ref that only refreshes on render, so an item can
   * still look 'queued' moments after it finished -- releasing the claim would
   * let a worker pick it up and upload it twice. A retry clears the claim,
   * which is the only thing that should make an item eligible again.
   */
  const claimed = useRef<Set<string>>(new Set());

  /**
   * Ids the user has cancelled.
   *
   * Kept in a ref rather than read from state because the upload loop needs to
   * see a cancellation that happened mid-transfer, and the closure it runs in
   * captured the old state.
   */
  const cancelled = useRef<Set<string>>(new Set());

  /** Live requests, so cancelling does not wait for the current chunk. */
  const controllers = useRef<Map<string, AbortController>>(new Map());

  const update = useCallback((id: string, patch: Partial<UploadItem>): void => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  /**
   * Folder ids by directory, shared by every file heading there.
   *
   * Holds the in-flight promise rather than the result, so four hundred photos
   * in one folder produce one request and the other three hundred and
   * ninety-nine await it instead of racing to create the same folder.
   */
  const folderCache = useRef<Map<string, Promise<string | null>>>(new Map());

  const resolveFolder = useCallback(
    (segments: string[], destination: string | null): Promise<string | null> => {
      if (segments.length === 0) return Promise.resolve(destination);

      // A pipe separates the two halves unambiguously: the destination is a
      // UUID or empty, so it can never contain one, and the first pipe is
      // always the boundary however the folder names are spelled.
      const key = `${destination ?? ''}|${segments.join('/')}`;
      const cached = folderCache.current.get(key);
      if (cached) return cached;

      const pending = api.ensureFolder(segments, destination).then((res) => res.folderId);
      folderCache.current.set(key, pending);

      // A failure must not be cached as a permanent answer, or retrying the
      // file would keep replaying the same error without ever asking again.
      void pending.catch(() => folderCache.current.delete(key));

      return pending;
    },
    [],
  );

  const uploadOne = useCallback(
    async (item: UploadItem): Promise<void> => {
      const controller = new AbortController();
      controllers.current.set(item.id, controller);

      try {
        update(item.id, { status: 'hashing', message: undefined });
        const contentHash = await sha256Hex(item.file);

        // Hashing a large file takes a while and cannot be interrupted, so the
        // cancellation may have arrived during it.
        if (cancelled.current.has(item.id)) return;

        // Resolved here rather than at queue time, and shared with every
        // other file bound for the same directory.
        let folderId: string | null;
        try {
          folderId = await resolveFolder(item.segments, item.destination);
        } catch (err) {
          const reason = err instanceof Error ? err.message : 'Could not create folder';
          update(item.id, { status: 'error', message: `Folder: ${reason}` });
          return;
        }

        if (cancelled.current.has(item.id)) return;

        const session = await api.createUpload({
          filename: item.file.name,
          size: item.file.size,
          folderId,
          ...(contentHash ? { contentHash } : {}),
        });

        if (session.deduplicated) {
          update(item.id, {
            status: 'duplicate',
            sent: item.file.size,
            assetId: session.assetId,
            // A linked file did land where it was aimed, so saying only that
            // it was a duplicate would read as "nothing happened" when in fact
            // the folder now contains it.
            message: session.linked
              ? `Already stored as "${session.existingFilename}"; added to this folder without re-uploading`
              : `Already in the archive as "${session.existingFilename}"`,
          });
          return;
        }

        update(item.id, { status: 'uploading', uploadId: session.uploadId, sent: session.offset });

        let offset = session.offset;
        const chunkSize = session.chunkSize;

        while (offset < item.file.size) {
          if (cancelled.current.has(item.id)) {
            // Tell the server too, so the partial temp file is deleted rather
            // than left occupying disk until something sweeps it up.
            void api.abortUpload(session.uploadId).catch(() => {});
            return;
          }

          const end = Math.min(offset + chunkSize, item.file.size);
          const slice = item.file.slice(offset, end);

          const chunkStart = offset;
          let lastReport = 0;

          try {
            const result = await api.uploadChunk(session.uploadId, offset, slice, {
              signal: controller.signal,
              onProgress: (sentInChunk) => {
                // Throttled: progress events fire far more often than the eye
                // can use, and each one re-renders the transfer list.
                const now = Date.now();
                if (now - lastReport < 200) return;
                lastReport = now;
                update(item.id, { sent: chunkStart + sentInChunk });
              },
            });
            offset = result.offset;
          } catch (err) {
            // The server reports its authoritative offset on a mismatch, so a
            // resumed or partially received chunk can rewind instead of
            // failing the whole transfer.
            const expected = (err as ApiRequestError)?.details as
              | { expectedOffset?: number }
              | undefined;
            if (err instanceof ApiRequestError && typeof expected?.expectedOffset === 'number') {
              offset = expected.expectedOffset;
              update(item.id, { sent: offset });
              continue;
            }
            throw err;
          }

          update(item.id, { sent: offset });
        }

        update(item.id, { status: 'finalizing' });
        const done = await api.completeUpload(session.uploadId);
        update(item.id, {
          status: done.deduplicated ? 'duplicate' : 'done',
          assetId: done.assetId,
          message: done.deduplicated
            ? 'Identical file already stored; linked to the existing copy.'
            : 'Processing metadata and thumbnails…',
        });
      } catch (err) {
        // An aborted request throws; that is the cancellation working, not a
        // failure, and it must not be offered for retry as though it were one.
        if (cancelled.current.has(item.id)) return;

        update(item.id, {
          status: 'error',
          message: err instanceof Error ? err.message : 'Upload failed',
        });
      } finally {
        controllers.current.delete(item.id);
      }
    },
    [update, resolveFolder],
  );

  /**
   * Works the queue until nothing is left.
   *
   * Re-entrant by design: enqueueing more files or retrying a failure while
   * transfers are running just tops the queue up, and the guard keeps a second
   * pump from doubling the concurrency.
   */
  /** Workers alive right now. The pool is resized against this. */
  const workers = useRef(0);

  const takeNext = useCallback((): UploadItem | null => {
    const item = nextUploadable(itemsRef.current, claimed.current, cancelled.current);
    if (!item) return null;
    // Claimed before any await, so two workers cannot take the same file.
    claimed.current.add(item.id);
    return item;
  }, []);

  /**
   * Brings the pool up to the configured size.
   *
   * Workers are spawned individually rather than as a fixed batch awaited
   * together, for two reasons. Waiting for a whole batch leaves slots idle
   * behind the slowest file in it, which for one large video among photos is
   * most of the transfer. And it makes the size adjustable while running:
   * turning concurrency up during a long migration starts more transfers
   * immediately instead of at some unspecified later batch, which is the only
   * time anyone would want to change it.
   */
  const pump = useCallback((): void => {
    // The availability check is load-bearing, not an optimisation. A worker
    // that finds nothing to do returns synchronously -- it never reaches an
    // await -- so its finally block decrements the count before this loop
    // re-tests it. Spawning purely on the count therefore spins forever
    // whenever there is less work than the concurrency setting, which is every
    // small batch and the tail of every large one. It locks the tab, and the
    // one upload that did get claimed never progresses because the event loop
    // is blocked.
    while (
      workers.current < concurrencyRef.current &&
      nextUploadable(itemsRef.current, claimed.current, cancelled.current) !== null
    ) {
      workers.current += 1;

      void (async () => {
        try {
          for (;;) {
            // Concurrency may have been lowered under us; retire quietly.
            if (workers.current > concurrencyRef.current) return;

            const item = takeNext();
            if (!item) return;

            // uploadOne records its own failures, so one bad file cannot take
            // the worker -- or any other file -- down with it.
            await uploadOne(item);
          }
        } finally {
          workers.current -= 1;
        }
      })();
    }
  }, [takeNext, uploadOne]);

  /**
   * Starts the queue whenever there is work and a free slot.
   *
   * Driven by state rather than called after enqueue, for two reasons. setItems
   * does not apply immediately and the ref the pool reads only refreshes on
   * render, so a pump() on the line after setItems inspects the list from
   * before the files were added and finds nothing to do. And having exactly one
   * trigger means there is no path where files can be queued without anything
   * starting them -- which is precisely what happened when this effect was
   * deleted: enqueue filled the list, nothing called pump, and the queue sat
   * full and idle with no error anywhere.
   */
  useEffect(() => {
    if (items.some((item) => item.status === 'queued' && !claimed.current.has(item.id))) {
      pump();
    }
    // concurrency is a dependency so raising it takes effect at once, rather
    // than whenever the next file happens to be queued.
  }, [items, concurrency, pump]);

  const enqueue = useCallback(
    (candidates: UploadCandidate[], destination: string | null): void => {
      if (candidates.length === 0) return;

      // Deliberately synchronous. Resolving every subdirectory up front meant
      // one sequential round trip per folder before a single byte moved, so a
      // dropped tree of two hundred folders sat still for as long as the
      // network took to answer two hundred times -- and it got worse the more
      // was dropped, which is the opposite of what a bulk upload should do.
      // Folders are resolved on demand instead, overlapping with the transfers
      // rather than blocking them.
      const queued: UploadItem[] = candidates.map((c) => ({
        id: `${c.segments.join('/')}/${c.file.name}-${c.file.size}-${c.file.lastModified}-${Math.random()
          .toString(36)
          .slice(2, 8)}`,
        file: c.file,
        segments: c.segments,
        destination,
        status: 'queued' as UploadStatus,
        sent: 0,
      }));

      setItems((prev) => [...prev, ...queued]);
    },
    [],
  );

  /**
   * Stops one upload.
   *
   * Three things have to happen and none of them is optional. The flag goes up
   * first so the loop stops at its next check even if the abort races it; the
   * in-flight request is torn down so a 64 MiB chunk does not have to finish
   * before anything visibly happens; and the server is told, so the partial
   * temp file is deleted rather than sitting on disk. That last one matters
   * during a large migration, where abandoned parts are measured in gigabytes.
   */
  const cancel = useCallback((id: string): void => {
    const item = itemsRef.current.find((entry) => entry.id === id);
    if (!item || !ACTIVE_STATUSES.has(item.status)) return;

    cancelled.current.add(id);
    controllers.current.get(id)?.abort();
    controllers.current.delete(id);

    if (item.uploadId) void api.abortUpload(item.uploadId).catch(() => {});

    setItems((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, status: 'cancelled', message: 'Cancelled' } : entry,
      ),
    );
  }, []);

  const cancelAll = useCallback((): void => {
    for (const item of itemsRef.current) {
      if (ACTIVE_STATUSES.has(item.status)) cancel(item.id);
    }
  }, [cancel]);

  const retry = useCallback(
    (id: string): void => {
      // Both have to be released, or the item is either skipped by the queue
      // or abandoned on its first cancellation check.
      cancelled.current.delete(id);
      claimed.current.delete(id);

      setItems((prev) =>
        prev.map((item) =>
          item.id === id && (item.status === 'error' || item.status === 'cancelled')
            ? { ...item, status: 'queued', sent: 0, message: undefined }
            : item,
        ),
      );
      pump();
    },
    [pump],
  );

  const retryAllFailed = useCallback((): void => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.status !== 'error' && item.status !== 'cancelled') return item;
        cancelled.current.delete(item.id);
        claimed.current.delete(item.id);
        return { ...item, status: 'queued' as UploadStatus, sent: 0, message: undefined };
      }),
    );
    pump();
  }, [pump]);

  const clearFinished = useCallback((): void => {
    setItems((prev) => {
      const kept = prev.filter(
        (item) =>
          ACTIVE_STATUSES.has(item.status) ||
          item.status === 'error' ||
          item.status === 'cancelled',
      );
      // Nothing left referring to the dropped rows, so their cancellation
      // flags would just accumulate for the life of the tab.
      const keptIds = new Set(kept.map((item) => item.id));
      for (const id of cancelled.current) if (!keptIds.has(id)) cancelled.current.delete(id);
      for (const id of claimed.current) if (!keptIds.has(id)) claimed.current.delete(id);
      return kept;
    });
  }, []);

  const remove = useCallback((id: string): void => {
    setItems((prev) => prev.filter((item) => item.id !== id || ACTIVE_STATUSES.has(item.status)));
  }, []);

  const activeCount = items.filter((item) => ACTIVE_STATUSES.has(item.status)).length;
  const failedCount = items.filter(
    (item) => item.status === 'error' || item.status === 'cancelled',
  ).length;

  const progress = useMemo(() => computeProgress(items), [items]);

  // Navigating inside the app is safe now, but closing the tab still ends
  // every transfer. Worth a prompt when a 300 GB migration is in progress.
  useEffect(() => {
    if (activeCount === 0) return undefined;

    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      // Browsers show their own wording; a non-empty value is what triggers it.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [activeCount]);

  const value = useMemo(
    () => ({
      items,
      enqueue,
      concurrency,
      setConcurrency,
      retry,
      retryAllFailed,
      cancel,
      cancelAll,
      clearFinished,
      remove,
      activeCount,
      failedCount,
      progress,
    }),
    [
      items,
      enqueue,
      concurrency,
      setConcurrency,
      retry,
      retryAllFailed,
      cancel,
      cancelAll,
      clearFinished,
      remove,
      activeCount,
      failedCount,
      progress,
    ],
  );

  return createElement(UploadContext.Provider, { value }, children);
}

export function useUploads(): UploadContextValue {
  const ctx = useContext(UploadContext);
  if (!ctx) throw new Error('useUploads must be used inside an UploadProvider');
  return ctx;
}
